import Backdrop from '@mui/material/Backdrop'
import { ComposerMediaContext } from 'components/ComposerMediaContext'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { addComposerImages, addComposerVideo, videoFileAdded } from 'services/compose'
import { composerTabsSelector } from 'services/compose/selectors'
import { message } from 'services/snackbar'
import { MAX_COMPOSER_PHOTOS_COUNT_FACEBOOK, MAX_COMPOSER_PHOTOS_COUNT_TWITTER } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { ALLOWED_PHOTO_FORMATS, ALLOWED_VIDEO_FORMATS } from 'utils/file'
import styles from './ComposerFilesDropHandler.pcss'
import { BRAND_FACEBOOK, BRAND_TWITTER } from 'shared'

interface ComposerFilesDropHandlerProps {
  visible: boolean
  onClose: () => void
}

export function ComposerFilesDropHandler(props: ComposerFilesDropHandlerProps) {
  const intl = useIntl()
  const { visible, onClose } = props
  const storeDispatch = useDispatch<StoreThunkDispatch>()
  const { dispatch } = React.useContext(ComposerMediaContext)
  const tabs = useSelector(composerTabsSelector)

  const handleAddedFiles = React.useCallback((files: FileList) => {
    const filesMap: { [name: string]: File } = {}
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i)
      if (file) {
        filesMap[file.name] = file
      }
    }

    const filesTotalCount = Object.keys(filesMap).length

    if (filesTotalCount > 0) {
      const photos: File[] = []
      const videos: File[] = []

      Object.values(filesMap).forEach(file => {
        if (ALLOWED_PHOTO_FORMATS.includes(file.type)) {
          photos.push(file)
        } else if (ALLOWED_VIDEO_FORMATS.includes(file.type)) {
          videos.push(file)
        }
      })

      if (photos.length + videos.length === 0) {
        storeDispatch(message(intl.formatMessage({ id: 'composer.notifications.dropped-invalid-files' }), 'warning'))
        onClose()
        return
      }

      if (photos.length + videos.length < filesTotalCount) {
        storeDispatch(message(intl.formatMessage({ id: 'composer.notifications.dropped-some-invalid-files' }), 'warning'))
      }

      if (tabs.includes(BRAND_TWITTER) && photos.length > MAX_COMPOSER_PHOTOS_COUNT_TWITTER) {
        storeDispatch(message(intl.formatMessage(
          { id: 'composer.notifications.tw-photos-count-limit-hit' },
          { count: MAX_COMPOSER_PHOTOS_COUNT_TWITTER }
        ), 'warning'))
      }

      if (tabs.includes(BRAND_FACEBOOK) && photos.length > MAX_COMPOSER_PHOTOS_COUNT_FACEBOOK) {
        storeDispatch(message(intl.formatMessage(
          { id: 'composer.notifications.fb-photos-count-limit-hit' },
          { count: MAX_COMPOSER_PHOTOS_COUNT_FACEBOOK }
        ), 'warning'))
      }

      if (photos.length > 0) {
        const filesState = photos.map(photo => ({ file: photo }))
        storeDispatch(addComposerImages({ items: filesState, contextDispatch: dispatch }))
      } else if (videos.length > 1) {
        storeDispatch(message(intl.formatMessage({ id: 'composer.notifications.one-video-only' }), 'warning'))
      } else if (videos.length === 1) {
        storeDispatch(addComposerVideo({ video: { file: videos[0] }, contextDispatch: dispatch }))
        storeDispatch(videoFileAdded(videos[0]))
      }
    }
  }, [dispatch, intl, onClose, storeDispatch, tabs])

  const onPaste = React.useCallback(async (e: any) => {
    handleAddedFiles(e.clipboardData.files)
  }, [handleAddedFiles])

  React.useEffect(() => {
    document.addEventListener('paste', onPaste)
    return () => {
      document.removeEventListener('paste', onPaste)
    }
  }, [onPaste])

  const onDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
    handleAddedFiles(e.dataTransfer.files)
    onClose()
  }, [handleAddedFiles, onClose])

  return (
    <Backdrop open={visible} className={styles.backdrop}>
      <div className={styles.dropbox} onDrop={onDrop} onClick={onClose} data-testid="composer-dropbox">
        <div className={styles.border}>
          <h3 className={styles.title}>
            <FormattedMessage id="composer.labels.drop-hint" />
          </h3>
        </div>
      </div>
    </Backdrop>
  )
}

export default ComposerFilesDropHandler
